/**
 * About DDARP — single-scroll narrative of the protocol, the substrate,
 * the patent IP, and the roadmap. Split from the original About.tsx
 * on 2026-05-26 in Public Pages Refinements Phase 5; the company
 * narrative now lives at /about-us.
 *
 * Section anchors (must match App.tsx legacy redirects):
 *   #thesis           – What DDARP is
 *   #thesis-detail    – Observation thesis (legacy /about/technology)
 *   #architecture     – 6-layer overview
 *   #substrate        – 118-node multi-cloud (legacy /about/project)
 *   #neutrality       – Operational neutrality (Patent Claim 11)
 *   #patent           – 91-claim USPTO provisional
 *   #roadmap          – Trajectory to 751 nodes
 *
 * Company-side anchors (#company, #engineering) trigger a client-side
 * navigate to /about-us#<anchor> so legacy bookmarks survive the split.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  GlowCard,
  GradientText,
  PublicCTAPair,
} from '@/components/common';
import {
  FileText,
  Radar,
  Layers,
  Globe,
  ShieldCheck,
  Map,
} from 'lucide-react';

// ─── Data ────────────────────────────────────────────────────────────────────

const SIX_LAYERS = [
  {
    n: 1,
    title: 'Composite Observation',
    blurb:
      'OWL one-way-latency probes at 1 Hz across the full mesh produce the atomic observation primitive. The result is a synchronized N-by-N latency matrix per measurement epoch.',
  },
  {
    n: 2,
    title: 'PulseGroup Formation',
    blurb:
      'Adaptive k-means clustering on the latency matrix groups nodes into measurement domains where within-group continuity is dense and stable.',
  },
  {
    n: 3,
    title: 'BorderNode Stitching',
    blurb:
      'BorderNodes participate in multiple PulseGroups. Their overlapping observation surfaces compose local continuity into a probabilistic global geometry.',
  },
  {
    n: 4,
    title: 'AnchorNode Synchronization',
    blurb:
      'A planned 65-node hardware spine (GNSS plus atomic plus holdover) provides a sub-2 ms global timing envelope so every observation has a defensible epoch.',
  },
  {
    n: 5,
    title: 'State Inference',
    blurb:
      'Behavioral baselines and Deviation Signatures classify continuity ruptures across corridors, surfacing anomalies before downstream impact propagates.',
  },
  {
    n: 6,
    title: 'Publication',
    blurb:
      'Four canonical stream classes (Substrate, Inference, Signature, Continuity) externalize the observation state to consumers. DDARP publishes; it never decides on their behalf.',
  },
];

const SUBSTRATE_BREAKDOWN = [
  { label: 'AWS', count: 48, color: 'bg-orange-500' },
  { label: 'Azure', count: 45, color: 'bg-sky-500' },
  { label: 'GCP', count: 25, color: 'bg-emerald-500' },
];

const ROADMAP_ITEMS = [
  {
    horizon: 'Live',
    title: '118-node multi-cloud substrate',
    detail:
      'AWS, GCP, and Azure across 6 continents. 13,806 full-mesh probe tunnels. Wave 13 cutover 2026-02-09.',
    pillClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  {
    horizon: 'Near term',
    title: 'AnchorNode timing spine',
    detail:
      '65 hardware nodes (GNSS plus atomic plus holdover), approximately $3.575M capex, providing a sub-2 ms global timing envelope.',
    pillClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    horizon: 'Mid term',
    title: 'API-driven density expansion',
    detail:
      '568 additional observation nodes via cloud APIs to reach the 751-node target topology.',
    pillClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    horizon: 'Mid term',
    title: 'Publication Layer GA',
    detail:
      'Four canonical stream classes available to subscribers. The streaming-API monetization tier opens.',
    pillClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
];

// ─── Subcomponents ──────────────────────────────────────────────────────────

function SectionAnchor({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      {children}
    </section>
  );
}

function SectionHeader({
  icon: Icon,
  eyebrow,
  title,
  subtitle,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
        <Icon size={14} />
        {eyebrow}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{title}</h2>
      {subtitle && (
        <p className="text-gray-600 text-lg max-w-3xl leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AboutDDARP() {
  const location = useLocation();
  const navigate = useNavigate();

  // Phase 5 fallback — legacy bookmarks at /about-ddarp#company or
  // /about-ddarp#engineering belong on /about-us now. Redirect.
  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    if (id === 'company' || id === 'engineering') {
      navigate(`/about-us#${id}`, { replace: true });
      return;
    }
    // Same-page anchor scroll. rAF + 260 ms retry handles framer-motion
    // settle where elements measure height 0 at first paint.
    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    requestAnimationFrame(() => {
      scrollTo();
      const t = window.setTimeout(scrollTo, 260);
      return () => window.clearTimeout(t);
    });
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* ─── Top brand banner (Higgsfield continuity-field) ──────────── */}
      <div className="relative w-full h-48 md:h-64 lg:h-72 overflow-hidden bg-[#0A1929]">
        <img
          src="/images/hero/hero-continuity-field.webp"
          alt=""
          aria-hidden
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white pointer-events-none" />
      </div>

      {/* ─── Page hero ───────────────────────────────────────────────── */}
      <section className="relative pt-12 pb-12 bg-gradient-to-b from-primary-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/20 shadow-sm mb-6"
              >
                <Radar size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">About DDARP</span>
              </motion.div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                <GradientText text="A canonical reference layer for the Internet" />
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                DDARP, the Distributed Detection And Routing Protocol, is a
                synchronized observation substrate for Internet continuity. This
                page covers what it is, how it is built, the IP behind it, and
                where it is heading.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── §1 What DDARP is (#thesis) ──────────────────────────────── */}
      <SectionAnchor id="thesis">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={Radar}
            eyebrow="The thesis"
            title="What DDARP is"
            subtitle="Doppler radar for the Internet. A synchronized, replayable, externalized observation layer, never a router, never a control plane."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <GlowCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What it IS</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• A multi-vantage observation substrate across 118 deployed nodes</li>
                <li>• An append-only continuity memory with cryptographic lineage</li>
                <li>• A probabilistic continuity surface, replayable across epochs</li>
                <li>• A publication interface for four canonical stream classes</li>
              </ul>
            </GlowCard>
            <GlowCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What it is NOT</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Not a router. It never selects paths for user traffic</li>
                <li>• Not a BGP participant. It never modifies routing control semantics</li>
                <li>• Not a control plane. It never injects, withdraws, or steers</li>
                <li>• Not a replacement for NetFlow, sFlow, or SNMP. It is a layer above</li>
              </ul>
            </GlowCard>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              ['118', 'deployed nodes'],
              ['13,806', 'mesh probe tunnels'],
              ['91', 'patent claims'],
              ['6', 'architecture layers'],
            ].map(([n, label]) => (
              <div
                key={label}
                className="text-center rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary">{n}</div>
                <div className="text-xs text-slate-500 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §2 The observation thesis (#thesis-detail) ──────────────── */}
      <SectionAnchor id="thesis-detail">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={Globe}
            eyebrow="Why this exists"
            title="The observation thesis"
            subtitle="The Internet has no canonical, synchronized, replayable reference for its own continuity. DDARP is that reference."
          />

          <div className="prose prose-slate max-w-none space-y-4 text-gray-700 leading-relaxed">
            <p>
              Internet observability today is fragmented. Every operator,
              vendor, and cloud reports its own slice of state through its own
              tooling (NetFlow, sFlow, BGP-LS, BMP, SNMP, RUM telemetry). No
              single consumer can reconstruct global continuity from those
              slices. They are not synchronized, not replayable, not composed
              under a common schema, and not externally verifiable.
            </p>
            <p>
              DDARP composes its own atomic observations (OWL one-way-latency
              probes at 1 Hz across a full mesh) with existing telemetry sources
              into a single externalized observation surface. Every measurement
              is append-only and cryptographically lineaged. The surface is
              replayable. A consumer can ask &quot;what did the Internet look
              like at 14:32:07 UTC on March 11&quot; and receive a deterministic
              answer.
            </p>
            <p>
              The positioning analogies are deliberate. Doppler radar for the
              Internet means a synchronized, scanning, externalized sensor
              lattice. Bloomberg Terminal of Internet continuity intelligence
              means a canonical publication surface that becomes infrastructure
              for everyone else. DDARP is consumed by hyperscalers, CDNs, SOCs,
              financial-risk teams, and sovereign continuity programs, never as
              a control plane, only as ground truth.
            </p>
          </div>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §3 The 6-layer architecture (#architecture) ─────────────── */}
      <SectionAnchor id="architecture">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={Layers}
            eyebrow="Architecture"
            title="The six-layer stack"
            subtitle="Each layer is a transform that composes atomic observations into a probabilistic global continuity geometry, then externalizes that geometry."
          />

          <StaggerContainer className="space-y-3">
            {SIX_LAYERS.map((layer) => (
              <StaggerItem key={layer.n}>
                <div className="flex gap-4 rounded-xl border border-slate-200 bg-white p-5 hover:border-primary/30 hover:shadow-sm transition-all">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-50 text-primary font-mono font-semibold flex items-center justify-center">
                    {layer.n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {layer.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{layer.blurb}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §4 The 118-node substrate (#substrate) ──────────────────── */}
      <SectionAnchor id="substrate">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="relative w-full h-40 md:h-52 rounded-xl overflow-hidden bg-[#0A1929] mb-10">
            <img
              src="/images/hero/hero-substrate-network.webp"
              alt=""
              aria-hidden
              className="w-full h-full object-cover opacity-95"
            />
          </div>

          <SectionHeader
            icon={Map}
            eyebrow="Substrate"
            title="118 nodes across three clouds and six continents"
            subtitle="The observation fabric is operational today, deployed across the three hyperscale clouds. The full mesh produces 13,806 probe tunnels. Every node measures every other node, every second."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {SUBSTRATE_BREAKDOWN.map((cloud) => (
              <div
                key={cloud.label}
                className="rounded-xl border border-slate-200 bg-white p-6"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-700">{cloud.label}</span>
                  <span className={'w-2 h-2 rounded-full ' + cloud.color} />
                </div>
                <div className="text-4xl font-bold text-gray-900">{cloud.count}</div>
                <div className="text-xs text-slate-500 mt-1">observation nodes</div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Deployment timeline (Waves 1 through 13)
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              The substrate was rolled out across 13 deployment waves between
              January and February 2026. Wave 1 (Virginia canary) shipped
              2026-01-21. Wave 13 (the final 12 nodes including AWS Calgary,
              Vancouver, and São Paulo expansion alongside Azure Hong Kong,
              UAE, and Osaka) shipped 2026-02-09. Every node runs the same
              observation daemon, the same OWL probing schedule, and the same
              full-mesh peering. Operational uniformity is a baseline invariant.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">
                Deployed
              </div>
              <div className="text-2xl font-bold text-gray-900">118</div>
              <div className="text-xs text-slate-600 mt-1">hyperscaler observation nodes</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">
                Planned
              </div>
              <div className="text-2xl font-bold text-gray-900">65</div>
              <div className="text-xs text-slate-600 mt-1">anchor hardware nodes</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">
                Planned
              </div>
              <div className="text-2xl font-bold text-gray-900">568</div>
              <div className="text-xs text-slate-600 mt-1">API-driven density nodes</div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Target topology: 751 nodes. Source: V2 Enterprise Documentation §7.1.
          </p>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §5 Operational neutrality (#neutrality) ─────────────────── */}
      <SectionAnchor id="neutrality">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={ShieldCheck}
            eyebrow="Operational neutrality"
            title="DDARP observes, never decides"
            subtitle="Operational neutrality is not a policy. It is a structural invariant of the system and a verbatim claim of the patent."
          />

          <div className="rounded-xl border-l-4 border-l-primary bg-slate-50 p-6 mb-6">
            <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-3">
              Patent Claim 11 (verbatim)
            </p>
            <p className="text-base text-slate-700 leading-relaxed italic">
              &quot;The system of claim 1, wherein the system remains observational
              infrastructure, exposes continuity intelligence outputs through
              publication interfaces only, and does not modify external routing
              control semantics or external forwarding behavior in the networks
              under observation.&quot;
            </p>
          </div>

          <p className="text-gray-700 leading-relaxed">
            Three consequences follow from operational neutrality. First,
            regulatory clarity. DDARP can be deployed alongside competing
            networks without becoming a competitor to them because it does
            not interfere with their control planes. Second, deployability.
            An ISP, hyperscaler, or sovereign program can adopt DDARP
            intelligence without restructuring its own routing. Third,
            liability containment. DDARP cannot cause a forwarding failure;
            downstream consumers decide what, if anything, to do with the
            signals it publishes.
          </p>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §6 IP & Patents (#patent) ───────────────────────────────── */}
      <SectionAnchor id="patent">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={FileText}
            eyebrow="Intellectual property"
            title="91-claim USPTO provisional"
            subtitle={
              'Filed under the title "Replayable Probabilistic Continuity Intelligence Architecture for Longitudinal Internet-State Reconstruction, Propagation Continuity Analysis, and Overlap-Preserving Distributed Observation Geometry."'
            }
          />

          <div className="rounded-xl border-l-4 border-l-primary bg-slate-50 p-6 mb-6">
            <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-3">
              Claim 1 (verbatim). Distributed Continuity Intelligence System.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed italic">
              &quot;A distributed continuity intelligence system comprising: (a) a
              plurality of distributed observation nodes positioned across two or
              more distributed network domains and organized into a plurality of
              coherence-groups… (b) a synchronization continuity subsystem… (c) a
              replay continuity subsystem… (d) an overlap continuity subsystem
              configured to compose local continuity surfaces… into a probabilistic
              global continuity geometry, the composition being mediated by one or
              more BorderNodes… and (e) a probabilistic continuity inference
              subsystem… wherein the system preserves deterministic replay
              continuity and explicit probabilistic uncertainty continuity
              throughout reconstruction.&quot;
            </p>
            <p className="text-xs text-slate-500 mt-3">
              The master apparatus claim. Every dependent claim refines an aspect of this structure.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              ['91', 'Total claims'],
              ['8', 'Independent claims'],
              ['12', 'Figures'],
              ['Filed', 'Status'],
            ].map(([n, label]) => (
              <div
                key={label}
                className="text-center rounded-xl border border-slate-200 bg-white p-5"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary">{n}</div>
                <div className="text-xs text-slate-500 mt-1">{label}</div>
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            The 91 claims are organized into 8 independent families covering the
            distributed continuity intelligence system, the synchronization
            subsystem, the replay subsystem, the overlap continuity subsystem,
            the probabilistic inference subsystem, the publication interface,
            the causal-inference family, and the cryptographic-lineage family.
            The inventor expressly reserves the right to pursue all claims
            supported by the disclosure in subsequent non-provisional,
            continuation, continuation-in-part, divisional, PCT, or foreign
            filings.
          </p>
        </div>
      </SectionAnchor>

      <div className="border-t border-slate-100" />

      {/* ─── §7 The roadmap (#roadmap) ──────────────────────────────── */}
      <SectionAnchor id="roadmap">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <SectionHeader
            icon={Map}
            eyebrow="Roadmap"
            title="Trajectory to 751 nodes"
            subtitle="The substrate is live. The anchor spine and publication interface are on the roadmap. The IP is filed."
          />

          <div className="space-y-3">
            {ROADMAP_ITEMS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-4 rounded-xl border border-slate-200 bg-white p-5"
              >
                <span
                  className={
                    'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border h-fit whitespace-nowrap ' +
                    item.pillClass
                  }
                >
                  {item.horizon}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.detail}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-xs text-slate-500 mt-6">
            Source: V2 Enterprise Documentation §17. Capex figures and timing
            envelope per same reference.
          </p>
        </div>
      </SectionAnchor>

      {/* ─── Final CTA ───────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Want to know more?
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              We have built DDARP across 118 nodes and three clouds. If you
              would like a deeper conversation about the protocol, the
              substrate, or a partnership, we would love to hear from you.
            </p>
            <PublicCTAPair learnMoreHref="/about-us" />
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
